<template>
  <head>
    <meta charset="utf-8" />
    <title>CBHI | Member Bulk Upload</title>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <link rel="shortcut icon" href="assets/images/favicon.svg">
    <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
    <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
    <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
    <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
  </head>

  <body data-sidebar="dark">
  <div id="layout-wrapper">
    <NavigationBar/>

    <div class="main-content">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Member Bulk Upload - By Family</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript: void(0);">Members</a></li>
                    <li class="breadcrumb-item active">Bulk Upload</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 1: File Upload -->
          <div v-if="currentStep === 1" class="row justify-content-center">
            <div class="col-12 col-lg-8">
              <div class="card">
                <div class="card-header bg-white border-bottom">
                  <h4 class="card-title mb-1">Upload Members Excel File</h4>
                  <p class="mb-0">Upload an Excel (.xlsx) file containing member information grouped by families.</p>
                </div>

                <div class="card-body">
                  <!-- Alerts -->
                  <div v-if="alert.success" class="alert alert-success" role="alert">{{message}}</div>
                  <div v-if="alert.info" class="alert alert-primary" role="alert">{{message}}</div>
                  <div v-if="alert.failed" class="alert alert-danger" role="alert">{{message}}</div>

                  <!-- File Upload Area -->
                  <div class="dropzone-wrapper" @dragover.prevent @drop.prevent="handleDrop">
                    <div class="dropzone-desc">
                      <i class="bx bx-cloud-upload text-primary" style="font-size: 3rem;"></i>
                      <p class="h4 mb-2">Choose Excel file or drag and drop</p>
                      <p class="text-muted">Select your members Excel file (.xlsx format)</p>
                      <input ref="fileInput" type="file" accept=".xlsx,.xls" @change="handleFileSelect" class="d-none">
                      <button @click="$refs.fileInput.click()" class="btn btn-primary waves-effect waves-light">
                        <i class="bx bx-upload me-2"></i>Select Excel File
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 2: Plan Selection -->
          <div v-if="currentStep === 2" class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-white border-bottom">
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <h4 class="card-title mb-1">Select CBHI Plan</h4>
                      <p class="mb-0">Choose a plan for all families. Found {{ families.length }} families with {{ totalMembers }} members.</p>
                    </div>
                    <!-- Kebele Status Indicator -->
                    <div v-if="validatingKebeles" class="text-end">
                      <span class="badge bg-info">
                        <span class="spinner-border spinner-border-sm me-1" role="status" style="width: 12px; height: 12px;"></span>
                        Validating Kebeles: {{ kebeleValidationProgress }}/{{ totalKebelesToValidate }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="card-body">
                  <div v-if="loadingPlans" class="text-center py-5">
                    <div class="spinner-border text-primary"></div>
                    <p class="mt-2">Loading available plans...</p>
                  </div>

                  <div v-else class="row">
                    <div v-for="plan in availablePlans" :key="plan.plan_id" class="col-md-4 mb-3">
                      <div class="card border" :class="{ 'border-primary border-3': selectedPlan?.plan_id === plan.plan_id }"
                           @click="selectPlan(plan)" style="cursor: pointer;">
                        <div class="card-body">
                          <div class="d-flex justify-content-between align-items-start mb-2">
                            <h5 class="card-title mb-0">{{ plan.plan_name }}</h5>
                            <span v-if="selectedPlan?.plan_id === plan.plan_id" class="badge bg-primary">
                              <i class="bx bx-check"></i> Selected
                            </span>
                          </div>
                          <p class="text-muted small">{{ plan.plan_code }}</p>

                          <div class="mb-3">
                            <h4 class="text-primary mb-0">
                              {{ plan.is_free ? 'FREE' : 'ETB ' + parseFloat(plan.primary_annual_amount).toFixed(2) }}
                              <small class="text-muted" style="font-size: 14px;">/year</small>
                            </h4>
                          </div>

                          <div class="mb-2">
                            <small class="text-muted d-block">
                              <i class="bx bx-check text-success"></i>
                              {{ plan.coverage_summary }}
                            </small>
                          </div>

                          <div v-if="!plan.is_free" class="alert alert-info py-2 px-2 mb-0">
                            <small>
                              <strong>Additional members:</strong>
                              {{ plan.pricing.additional_member_rate_percentage }}%
                              (ETB {{ plan.pricing.adult_child_rate }})
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="card-footer bg-white border-top py-3">
                  <!-- Kebele Validation Progress Overlay -->
                  <div v-if="validatingKebeles" class="position-relative">
                    <div class="alert alert-info mb-3">
                      <div class="d-flex align-items-center justify-content-between">
                        <div class="d-flex align-items-center">
                          <div class="spinner-border spinner-border-sm text-primary me-3" role="status"></div>
                          <div>
                            <strong>Validating Kebeles...</strong>
                            <small class="d-block text-muted">
                              Checking {{ kebeleValidationProgress }} of {{ totalKebelesToValidate }} unique kebeles
                            </small>
                          </div>
                        </div>
                        <div class="text-end">
                          <strong class="text-primary">{{ Math.round((kebeleValidationProgress / totalKebelesToValidate) * 100) }}%</strong>
                        </div>
                      </div>
                      <div class="progress mt-2" style="height: 8px;">
                        <div class="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                             role="progressbar"
                             :style="{ width: ((kebeleValidationProgress / totalKebelesToValidate) * 100) + '%' }">
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="d-flex justify-content-between">
                    <button @click="goBack" class="btn btn-outline-secondary" :disabled="validatingKebeles">
                      <i class="bx bx-arrow-back me-2"></i>Back
                    </button>
                    <button @click="proceedToPreview"
                            :disabled="!selectedPlan || validatingKebeles"
                            class="btn btn-primary">
                      <span v-if="validatingKebeles">
                        <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                        Validating Kebeles...
                      </span>
                      <span v-else>
                        <i class="bx bx-arrow-to-right me-2"></i>Preview Families
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 3: Family Preview -->
          <div v-if="currentStep === 3" class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-white border-bottom">
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <h4 class="card-title mb-1">Preview Families & Expected Payments</h4>
                      <p class="mb-0">{{ families.length }} families • {{ totalMembers }} members • Plan: {{ selectedPlan.plan_name }}</p>
                    </div>
                    <div class="text-end">
                      <h5 class="mb-0 text-primary">Total Expected: ETB {{ totalExpectedPayment.toFixed(2) }}</h5>
                      <small class="text-muted">Across all families</small>
                    </div>
                  </div>
                </div>

                <div class="card-body">
                  <!-- Validation Summary -->
                  <div v-if="validationErrors.length > 0" class="alert alert-warning" role="alert">
                    <h5 class="alert-heading">Validation Issues</h5>
                    <p>{{ validationErrors.length }} issues found:</p>
                    <ul class="mb-0">
                      <li v-for="error in validationErrors.slice(0, 5)" :key="error">{{ error }}</li>
                      <li v-if="validationErrors.length > 5">...and {{ validationErrors.length - 5 }} more</li>
                    </ul>
                  </div>

                  <!-- Families List -->
                  <div class="accordion" id="familiesAccordion">
                    <div v-for="(family, index) in paginatedFamilies" :key="family.householdId" class="accordion-item mb-3">
                      <h2 class="accordion-header">
                        <button class="accordion-button" :class="{ collapsed: index !== 0 }" type="button"
                                :data-bs-toggle="'collapse'" :data-bs-target="'#family' + index">
                          <div class="w-100 d-flex justify-content-between align-items-center pe-3">
                            <div>
                              <strong>Family {{ (currentPage * itemsPerPage) + index + 1 }}</strong>
                              <span class="badge bg-primary ms-2">{{ family.householdId }}</span>
                              <span class="ms-2 text-muted">{{ family.members.length }} members</span>
                            </div>
                            <div class="text-end">
                              <strong class="text-success">ETB {{ family.expectedPayment.toFixed(2) }}</strong>
                              <small class="d-block text-muted">Expected payment</small>
                            </div>
                          </div>
                        </button>
                      </h2>
                      <div :id="'family' + index" class="accordion-collapse collapse" :class="{ show: index === 0 }">
                        <div class="accordion-body">
                          <div class="table-responsive">
                            <table class="table table-sm">
                              <thead>
                              <tr>
                                <th>Name</th>
                                <th>DOB</th>
                                <th>Gender</th>
                                <th>Relationship</th>
                                <th>Kebele</th>
                                <th>Status</th>
                              </tr>
                              </thead>
                              <tbody>
                              <tr v-for="member in family.members" :key="member.rowIndex"
                                  :class="getRowClass(member)">
                                <td>
                                  <strong v-if="member.relationship === 'head'">
                                    <i class="bx bx-star text-warning me-1"></i>{{ member.fullName }}
                                  </strong>
                                  <span v-else>{{ member.fullName }}</span>
                                </td>
                                <td>{{ formatDate(member.dateOfBirth) }}</td>
                                <td>{{ member.gender }}</td>
                                <td>
                                    <span :class="getRelationshipBadgeClass(member.relationship)">
                                      {{ member.relationship }}
                                    </span>
                                </td>
                                <td>{{ member.kebele }}</td>
                                <td>
                                    <span v-if="hasValidationError(member)" class="badge bg-danger">
                                      <i class="bx bx-x-circle"></i> Invalid
                                    </span>
                                  <span v-else class="badge bg-success">
                                      <i class="bx bx-check-circle"></i> Valid
                                    </span>
                                </td>
                              </tr>
                              </tbody>
                            </table>
                          </div>

                          <!-- Payment Breakdown -->
                          <div class="alert alert-light mb-0">
                            <strong>Payment Breakdown:</strong>
                            <div class="ms-3">
                              <div>Principal: ETB {{ parseFloat(selectedPlan.primary_annual_amount).toFixed(2) }}</div>
                              <div v-if="family.freeMembers > 0">Free members (immediate family): {{ family.freeMembers }}</div>
                              <div v-if="family.additionalMembers > 0">
                                Additional members: {{ family.additionalMembers }} × ETB {{ family.additionalMemberCost.toFixed(2) }}
                                = ETB {{ (family.additionalMembers * family.additionalMemberCost).toFixed(2) }}
                              </div>
                              <div class="mt-2"><strong>Total: ETB {{ family.expectedPayment.toFixed(2) }}</strong></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Pagination -->
                  <div class="row align-items-center mt-3">
                    <div class="col-sm-6">
                      <div class="dataTables_info">
                        Showing {{ (currentPage * itemsPerPage) + 1 }} to
                        {{ Math.min((currentPage + 1) * itemsPerPage, families.length) }}
                        of {{ families.length }} families
                      </div>
                    </div>
                    <div class="col-sm-6">
                      <div class="dataTables_paginate paging_simple_numbers float-end">
                        <ul class="pagination pagination-rounded">
                          <li class="paginate_button page-item" :class="{ disabled: currentPage === 0 }">
                            <a @click="currentPage = Math.max(0, currentPage - 1)" class="page-link">Previous</a>
                          </li>
                          <li v-for="page in visiblePages" :key="page"
                              class="paginate_button page-item"
                              :class="{ active: currentPage === page - 1 }">
                            <a @click="currentPage = page - 1" class="page-link">{{ page }}</a>
                          </li>
                          <li class="paginate_button page-item" :class="{ disabled: currentPage >= totalPages - 1 }">
                            <a @click="currentPage = Math.min(totalPages - 1, currentPage + 1)" class="page-link">Next</a>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="card-footer bg-white border-top py-3">
                  <!-- Upload Mode Selection -->
                  <div class="mb-4">
                    <label class="form-label fw-bold text-dark mb-3" style="font-size: 16px;">
                      <i class="bx bx-sync me-1 text-info"></i>
                      Upload Processing Mode<span class="text-danger">*</span>
                    </label>

                    <div class="row">
                      <div class="col-md-6 mb-3">
                        <div class="card border-2 processing-mode-option"
                             :class="{ 'border-info bg-info-subtle': uploadMode === 'realtime', 'border-secondary': uploadMode !== 'realtime' }"
                             @click="uploadMode = 'realtime'" style="cursor: pointer;">
                          <div class="card-body">
                            <div class="form-check">
                              <input class="form-check-input" type="radio" name="uploadMode" id="realtimeMode"
                                     value="realtime" v-model="uploadMode">
                              <label class="form-check-label fw-bold w-100" for="realtimeMode" style="cursor: pointer;">
                                <div class="d-flex align-items-center">
                                  <i class="bx bx-bolt-circle text-info me-2" style="font-size: 24px;"></i>
                                  <div>
                                    <div class="text-info fw-bold">Real-Time Processing</div>
                                    <small class="text-muted d-block">Process families immediately in browser. See live progress.</small>
                                  </div>
                                </div>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="col-md-6 mb-3">
                        <div class="card border-2 processing-mode-option"
                             :class="{ 'border-success bg-success-subtle': uploadMode === 'background', 'border-secondary': uploadMode !== 'background' }"
                             @click="uploadMode = 'background'" style="cursor: pointer;">
                          <div class="card-body">
                            <div class="form-check">
                              <input class="form-check-input" type="radio" name="uploadMode" id="backgroundMode"
                                     value="background" v-model="uploadMode">
                              <label class="form-check-label fw-bold w-100" for="backgroundMode" style="cursor: pointer;">
                                <div class="d-flex align-items-center">
                                  <i class="bx bx-cloud-upload text-success me-2" style="font-size: 24px;"></i>
                                  <div>
                                    <div class="text-success fw-bold">Background Processing</div>
                                    <small class="text-muted d-block">Upload to server. Can close browser. Get notified when done.</small>
                                  </div>
                                </div>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Mode Info Alert -->
                    <div v-if="uploadMode === 'realtime'" class="alert alert-info mb-0">
                      <i class="bx bx-info-circle me-2"></i>
                      <strong>Real-Time Mode:</strong> Keep this tab open until processing completes.
                    </div>
                    <div v-if="uploadMode === 'background'" class="alert alert-success mb-0">
                      <i class="bx bx-info-circle me-2"></i>
                      <strong>Background Mode:</strong> Processing happens on server. You can close the browser.
                    </div>
                  </div>

                  <!-- Payment Status Selection -->
                  <div class="mb-4">
                    <div class="alert alert-warning border-warning mb-3">
                      <h6 class="alert-heading mb-2">
                        <i class="bx bx-error-circle me-1"></i>
                        <strong>MANDATORY: Payment Status Required</strong>
                      </h6>
                      <p class="mb-0 small">You must select the payment status before proceeding.</p>
                    </div>

                    <label class="form-label fw-bold text-dark mb-3" style="font-size: 16px;">
                      <i class="bx bx-money me-1 text-primary"></i>
                      Have all members in this upload paid their premiums?<span class="text-danger">*</span>
                    </label>

                    <div class="row">
                      <div class="col-md-6 mb-3">
                        <div class="card border-2 payment-option" :class="{ 'border-success bg-success-subtle': paymentStatus === 'paid', 'border-secondary': paymentStatus !== 'paid' }"
                             @click="paymentStatus = 'paid'" style="cursor: pointer;">
                          <div class="card-body">
                            <div class="form-check">
                              <input class="form-check-input" type="radio" name="paymentStatus" id="paymentPaid"
                                     value="paid" v-model="paymentStatus">
                              <label class="form-check-label fw-bold w-100" for="paymentPaid" style="cursor: pointer;">
                                <div class="d-flex align-items-center">
                                  <i class="bx bx-check-circle text-success me-2" style="font-size: 24px;"></i>
                                  <div>
                                    <div class="text-success fw-bold">Yes, Members Have Paid</div>
                                    <small class="text-muted d-block">Members will be activated immediately.</small>
                                  </div>
                                </div>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="col-md-6 mb-3">
                        <div class="card border-2 payment-option" :class="{ 'border-primary bg-primary-subtle': paymentStatus === 'not_paid', 'border-secondary': paymentStatus !== 'not_paid' }"
                             @click="paymentStatus = 'not_paid'" style="cursor: pointer;">
                          <div class="card-body">
                            <div class="form-check">
                              <input class="form-check-input" type="radio" name="paymentStatus" id="paymentNotPaid"
                                     value="not_paid" v-model="paymentStatus">
                              <label class="form-check-label fw-bold w-100" for="paymentNotPaid" style="cursor: pointer;">
                                <div class="d-flex align-items-center">
                                  <i class="bx bx-x-circle text-primary me-2" style="font-size: 24px;"></i>
                                  <div>
                                    <div class="text-primary fw-bold">No, Payment Pending</div>
                                    <small class="text-muted d-block">Members will remain inactive until payment.</small>
                                  </div>
                                </div>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Payment Summary Alert -->
                    <div v-if="paymentStatus === 'paid'" class="alert alert-success mb-0">
                      <div class="d-flex align-items-center">
                        <i class="bx bx-check-circle font-size-24 me-2"></i>
                        <div>
                          <strong>Payment Confirmed:</strong> All {{ families.length }} families totaling
                          <strong>ETB {{ totalExpectedPayment.toFixed(2) }}</strong> will be activated.
                        </div>
                      </div>
                    </div>

                    <div v-if="paymentStatus === 'not_paid'" class="alert alert-info mb-0">
                      <div class="d-flex align-items-center">
                        <i class="bx bx-info-circle font-size-24 me-2"></i>
                        <div>
                          <strong>Payment Pending:</strong> Members will be registered but remain inactive.
                        </div>
                      </div>
                    </div>

                    <!-- Validation Warning -->
                    <div v-if="!paymentStatus || !uploadMode" class="alert alert-danger mt-3 mb-0">
                      <i class="bx bx-error me-2"></i>
                      <strong>Please select both upload mode and payment status before proceeding.</strong>
                    </div>
                  </div>

                  <!-- Action Buttons -->
                  <div class="d-flex justify-content-between align-items-center">
                    <button @click="currentStep = 2" class="btn btn-outline-secondary">
                      <i class="bx bx-arrow-back me-2"></i>Change Plan
                    </button>
                    <button @click="startUpload"
                            :disabled="validationErrors.length > 0 || processing || !paymentStatus || !uploadMode"
                            class="btn btn-lg"
                            :class="!paymentStatus || !uploadMode ? 'btn-secondary' :
                                   (paymentStatus === 'paid' ? 'btn-success' : 'btn-primary')">
                      <i class="bx me-2" :class="uploadMode === 'background' ? 'bx-cloud-upload' : 'bx-upload'"></i>
                      <span v-if="!paymentStatus || !uploadMode">Complete Selection First</span>
                      <span v-else>{{ uploadMode === 'background' ? 'Upload to Server' : 'Start Processing' }} ({{ families.length }} families)</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 4: Processing -->
          <div v-if="currentStep === 4" class="row justify-content-center">
            <div class="col-12 col-lg-10">
              <div class="card">
                <div class="card-header bg-white border-bottom">
                  <h4 class="card-title mb-1">
                    {{ uploadMode === 'background' ? 'Background Processing' : 'Uploading Families' }}
                  </h4>
                  <p class="mb-0">
                    {{ uploadMode === 'background'
                      ? 'Your file has been uploaded and is being processed on the server.'
                      : 'Please wait while we register all families and members...' }}
                  </p>
                </div>

                <div class="card-body">
                  <!-- Background Mode Info -->
                  <div v-if="uploadMode === 'background'" class="alert alert-info mb-4">
                    <h5 class="alert-heading">
                      <i class="bx bx-info-circle me-2"></i>Background Processing Active
                    </h5>
                    <p class="mb-2"><strong>Job ID:</strong> {{ backgroundJobId }}</p>
                    <p class="mb-0">You can safely close this page. Check the "Background Jobs" page for status updates.</p>


                  </div>

                  <!-- Upload Statistics Cards -->
                  <div class="row mb-4">
                    <div class="col-md-3 col-6 mb-3">
                      <div class="card bg-primary text-white">
                        <div class="card-body p-3">
                          <h6 class="mb-1 text-white">Total Families</h6>
                          <h3 class="mb-0 text-white">{{ families.length }}</h3>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-3 col-6 mb-3">
                      <div class="card bg-success text-white">
                        <div class="card-body p-3">
                          <h6 class="mb-1 text-white">Completed</h6>
                          <h3 class="mb-0 text-white">{{ uploadResults.successfulFamilies }}</h3>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-3 col-6 mb-3">
                      <div class="card bg-danger text-white">
                        <div class="card-body p-3">
                          <h6 class="mb-1 text-white">Failed</h6>
                          <h3 class="mb-0 text-white">{{ uploadResults.failedFamilies }}</h3>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-3 col-6 mb-3">
                      <div class="card bg-info text-white">
                        <div class="card-body p-3">
                          <h6 class="mb-1 text-white">Remaining</h6>
                          <h3 class="mb-0 text-white">{{ families.length - (uploadResults.successfulFamilies + uploadResults.failedFamilies) }}</h3>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Progress Bar -->
                  <div class="mb-4">
                    <div class="d-flex justify-content-between mb-2">
                      <span class="fw-bold">Overall Progress</span>
                      <span class="text-muted">{{ uploadProgress.toFixed(1) }}%</span>
                    </div>
                    <div class="progress" style="height: 30px;">
                      <div class="progress-bar progress-bar-striped"
                           :class="uploadPaused ? 'bg-warning' : 'progress-bar-animated bg-primary'"
                           role="progressbar"
                           :style="{ width: uploadProgress + '%' }">
                        <strong>{{ uploadProgress.toFixed(1) }}%</strong>
                      </div>
                    </div>
                  </div>

                  <!-- Time Information (Real-time mode only) -->
                  <div v-if="uploadMode === 'realtime'" class="row mb-4">
                    <div class="col-md-4">
                      <div class="d-flex align-items-center">
                        <i class="bx bx-time-five text-primary me-2" style="font-size: 24px;"></i>
                        <div>
                          <small class="text-muted d-block">Elapsed Time</small>
                          <strong>{{ formatTime(elapsedTime) }}</strong>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="d-flex align-items-center">
                        <i class="bx bx-hourglass text-info me-2" style="font-size: 24px;"></i>
                        <div>
                          <small class="text-muted d-block">Estimated Remaining</small>
                          <strong>{{ estimatedTimeRemaining }}</strong>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="d-flex align-items-center">
                        <i class="bx bx-check-circle text-success me-2" style="font-size: 24px;"></i>
                        <div>
                          <small class="text-muted d-block">Estimated Completion</small>
                          <strong>{{ estimatedCompletionTime }}</strong>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Upload Status Messages -->
                  <div v-if="uploadMode === 'realtime'">
                    <div v-if="uploadPaused" class="alert alert-warning" role="alert">
                      <i class="bx bx-pause-circle me-2"></i>
                      <strong>Upload Paused</strong> - Click Resume to continue.
                    </div>
                    <div v-else-if="uploadProgress < 100" class="alert alert-info" role="alert">
                      <div class="d-flex align-items-center">
                        <div class="spinner-border spinner-border-sm me-2"></div>
                        <strong>Processing family {{ currentProcessingFamily }} of {{ families.length }}</strong>
                      </div>
                    </div>

                    <!-- Control Buttons -->
                    <div class="d-flex justify-content-center gap-3 mb-4">
                      <button v-if="!uploadPaused && uploadProgress < 100"
                              @click="pauseUpload"
                              class="btn btn-warning btn-lg">
                        <i class="bx bx-pause me-2"></i>Pause Upload
                      </button>
                      <button v-if="uploadPaused"
                              @click="resumeUpload"
                              class="btn btn-success btn-lg">
                        <i class="bx bx-play me-2"></i>Resume Upload
                      </button>
                      <button v-if="uploadProgress < 100"
                              @click="cancelUpload"
                              class="btn btn-danger btn-lg">
                        <i class="bx bx-x me-2"></i>Cancel Upload
                      </button>
                    </div>

                    <!-- Current Family Processing -->
                    <div v-if="currentFamilyProcessing && uploadProgress < 100" class="card bg-light">
                      <div class="card-body">
                        <h6 class="card-title mb-2">Currently Processing:</h6>
                        <div class="d-flex justify-content-between align-items-center">
                          <div>
                            <strong>{{ currentFamilyProcessing.householdId }}</strong>
                            <span class="ms-2 text-muted">{{ currentFamilyProcessing.members.length }} members</span>
                          </div>
                          <div class="spinner-border spinner-border-sm text-primary"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Recent Activity -->
                  <div class="mt-4" v-if="recentActivity.length > 0">
                    <h6 class="mb-3">Recent Activity</h6>
                    <div class="activity-log" style="max-height: 300px; overflow-y: auto;">
                      <div v-for="(activity, index) in recentActivity" :key="index"
                           class="activity-item mb-2 p-2 border-start border-3"
                           :class="activity.success ? 'border-success bg-success-subtle' : 'border-danger bg-danger-subtle'">
                        <div class="d-flex justify-content-between align-items-start">
                          <div>
                            <i class="bx" :class="activity.success ? 'bx-check-circle text-success' : 'bx-x-circle text-danger'"></i>
                            <strong class="ms-2">{{ activity.householdId }}</strong>
                            <span class="ms-2 text-muted small">{{ activity.members?.length || 0 }} members</span>
                          </div>
                          <small class="text-muted">{{ activity.timestamp }}</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 5: Results -->
          <div v-if="currentStep === 5" class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-white border-bottom">
                  <h4 class="card-title mb-1">Upload Results</h4>
                  <p class="mb-0">Summary of family uploads</p>
                </div>

                <div class="card-body">
                  <!-- Summary Cards -->
                  <div class="row mb-4">
                    <div class="col-md-3">
                      <div class="card border-success">
                        <div class="card-body text-center">
                          <h4 class="text-success">{{ uploadResults.successfulFamilies }}</h4>
                          <p class="mb-0">Successful Families</p>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="card border-danger">
                        <div class="card-body text-center">
                          <h4 class="text-danger">{{ uploadResults.failedFamilies }}</h4>
                          <p class="mb-0">Failed Families</p>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="card border-info">
                        <div class="card-body text-center">
                          <h4 class="text-info">{{ uploadResults.successfulMembers }}</h4>
                          <p class="mb-0">Successful Members</p>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="card border-warning">
                        <div class="card-body text-center">
                          <h4 class="text-warning">{{ uploadResults.failedMembers }}</h4>
                          <p class="mb-0">Failed Members</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Total Time -->
                  <div v-if="uploadMode === 'realtime'" class="alert alert-info mb-4">
                    <i class="bx bx-time-five me-2"></i>
                    <strong>Total Upload Time:</strong> {{ formatTime(totalUploadTime) }}
                  </div>

                  <!-- Detailed Results -->
                  <div class="accordion" id="resultsAccordion">
                    <div v-for="(familyResult, index) in paginatedResults" :key="index" class="accordion-item mb-2">
                      <h2 class="accordion-header">
                        <button class="accordion-button collapsed" type="button"
                                :data-bs-toggle="'collapse'" :data-bs-target="'#result' + index">
                          <div class="w-100 d-flex justify-content-between align-items-center pe-3">
                            <div>
                              <span :class="familyResult.success ? 'badge bg-success' : 'badge bg-danger'">
                                {{ familyResult.success ? 'SUCCESS' : 'FAILED' }}
                              </span>
                              <strong class="ms-2">{{ familyResult.householdId }}</strong>
                              <span class="text-muted ms-2">({{ familyResult.members?.length || 0 }} members)</span>
                            </div>
                            <div>
                              <span class="text-success me-2">✓ {{ familyResult.successCount || 0 }}</span>
                              <span class="text-danger">✗ {{ familyResult.failureCount || 0 }}</span>
                            </div>
                          </div>
                        </button>
                      </h2>
                      <div :id="'result' + index" class="accordion-collapse collapse">
                        <div class="accordion-body">
                          <table class="table table-sm">
                            <thead>
                            <tr>
                              <th>Member</th>
                              <th>Relationship</th>
                              <th>Status</th>
                              <th>CBHI Number</th>
                              <th>Message</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="member in familyResult.members" :key="member.name"
                                :class="member.success ? 'table-success' : 'table-danger'">
                              <td>{{ member.name }}</td>
                              <td>{{ member.relationship }}</td>
                              <td>
                                  <span :class="member.success ? 'badge bg-success' : 'badge bg-danger'">
                                    {{ member.success ? 'Success' : 'Failed' }}
                                  </span>
                              </td>
                              <td>{{ member.cbhiNumber || '-' }}</td>
                              <td>{{ member.message }}</td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Results Pagination -->
                  <div class="row align-items-center mt-3">
                    <div class="col-sm-6">
                      <div class="dataTables_info">
                        Showing {{ (resultsPage * resultsPerPage) + 1 }} to
                        {{ Math.min((resultsPage + 1) * resultsPerPage, uploadResults.familyDetails.length) }}
                        of {{ uploadResults.familyDetails.length }} families
                      </div>
                    </div>
                    <div class="col-sm-6">
                      <div class="dataTables_paginate paging_simple_numbers float-end">
                        <ul class="pagination pagination-rounded">
                          <li class="paginate_button page-item" :class="{ disabled: resultsPage === 0 }">
                            <a @click="resultsPage--" class="page-link">Previous</a>
                          </li>
                          <li class="paginate_button page-item" :class="{ disabled: resultsPage >= totalResultsPages - 1 }">
                            <a @click="resultsPage++" class="page-link">Next</a>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="card-footer bg-white border-top py-3">
                  <div class="d-flex justify-content-between">
                    <button @click="resetUpload" class="btn btn-outline-secondary">
                      <i class="bx bx-upload me-2"></i>Upload Another File
                    </button>
                    <button @click="downloadResults" class="btn btn-outline-primary">
                      <i class="bx bx-download me-2"></i>Download Results
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-6"></div>
            <div class="col-sm-6">
              <div class="text-sm-end d-sm-block">Ministry of Health - Ethiopia</div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  </div>

  <KebeleMapping
      :show="showKebeleMapping"
      :mappings="kebeleMappings"
      @close="showKebeleMapping = false"
      @apply-mapping="applyKebeleMapping"
      @skip-rows="skipKebeleRows"
      @proceed="proceedAfterKebeleMapping"
      @show-alert="showAlert"
  />

  </body>
</template>

<script>
/* eslint-disable */

import KebeleMapping from "@/components/cbhi-member-management/bulk-upload/modals/KebeleMapping.vue";
import { authUrl, executeJson } from "@/api";
import * as XLSX from 'xlsx';
import NavigationBar from "@/components/Navigation.vue";

export default {
  name: "MemberBulkUploadComplete",
  components: {NavigationBar, KebeleMapping },

  data() {
    return {
      showKebeleMapping: false,
      kebeleMappings: [],
      kebeleMap: new Map(),
      skippedKebeles: new Set(),
      tempMembers: [],

      // Kebele validation progress
      validatingKebeles: false,
      kebeleValidationProgress: 0,
      totalKebelesToValidate: 0,

      currentStep: 1,
      selectedFile: null,

      // Upload mode
      uploadMode: 'realtime', // 'realtime' or 'background'
      backgroundJobId: null,
      pollingInterval: null,

      // Plans
      availablePlans: [],
      selectedPlan: null,
      loadingPlans: false,

      // Families and members
      families: [],
      totalMembers: 0,
      totalExpectedPayment: 0,
      validationErrors: [],

      // Processing
      processing: false,
      uploadPaused: false,
      uploadCancelled: false,
      processedCount: 0,
      currentProcessingFamily: 0,
      currentProcessingMember: 0,
      currentFamilyProcessing: null,
      uploadProgress: 0,

      // Time tracking
      uploadStartTime: null,
      uploadPauseTime: null,
      totalPausedTime: 0,
      elapsedTime: 0,
      timerInterval: null,

      // Results
      uploadResults: {
        successfulFamilies: 0,
        failedFamilies: 0,
        successfulMembers: 0,
        failedMembers: 0,
        familyDetails: []
      },

      recentActivity: [],

      // Pagination
      currentPage: 0,
      itemsPerPage: 5,
      resultsPage: 0,
      resultsPerPage: 10,

      // Alerts
      alert: { success: false, info: false, failed: false },
      message: '',

      // Payment status
      paymentStatus: null  // 'paid' or 'not_paid'
    };
  },

  computed: {
    totalPages() {
      return Math.ceil(this.families.length / this.itemsPerPage);
    },

    visiblePages() {
      const maxVisible = 5;
      const halfVisible = Math.floor(maxVisible / 2);
      let startPage = Math.max(1, this.currentPage + 1 - halfVisible);
      let endPage = Math.min(this.totalPages, startPage + maxVisible - 1);

      if (endPage - startPage < maxVisible - 1) {
        startPage = Math.max(1, endPage - maxVisible + 1);
      }

      const pages = [];
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      return pages;
    },

    paginatedFamilies() {
      const start = this.currentPage * this.itemsPerPage;
      return this.families.slice(start, start + this.itemsPerPage);
    },

    totalResultsPages() {
      return Math.ceil(this.uploadResults.familyDetails.length / this.resultsPerPage);
    },

    paginatedResults() {
      const start = this.resultsPage * this.resultsPerPage;
      return this.uploadResults.familyDetails.slice(start, start + this.resultsPerPage);
    },

    estimatedTimeRemaining() {
      if (this.uploadProgress === 0 || this.uploadPaused) return 'Calculating...';
      if (this.uploadProgress >= 100) return 'Complete';

      const processed = this.uploadResults.successfulFamilies + this.uploadResults.failedFamilies;
      const remaining = this.families.length - processed;

      if (processed === 0) return 'Calculating...';

      const avgTimePerFamily = this.elapsedTime / processed;
      const estimatedSeconds = Math.round(avgTimePerFamily * remaining);

      return this.formatTime(estimatedSeconds);
    },

    estimatedCompletionTime() {
      if (this.uploadProgress === 0 || this.uploadPaused) return 'N/A';
      if (this.uploadProgress >= 100) return 'Completed';

      const processed = this.uploadResults.successfulFamilies + this.uploadResults.failedFamilies;
      const remaining = this.families.length - processed;

      if (processed === 0) return 'Calculating...';

      const avgTimePerFamily = this.elapsedTime / processed;
      const estimatedSeconds = avgTimePerFamily * remaining;

      const completionTime = new Date(Date.now() + (estimatedSeconds * 1000));
      return completionTime.toLocaleTimeString();
    },

    totalUploadTime() {
      return this.elapsedTime;
    }
  },

  mounted() {
    this.checkAuth();
  },

  beforeUnmount() {
    this.stopTimer();
    this.stopPolling();
  },

  methods: {
    checkAuth() {
      const token = localStorage.getItem("authToken");
      if (!token) window.location.href = '/login';
    },

    clearAlerts() {
      this.alert = { success: false, info: false, failed: false };
    },

    showAlert(type, message) {
      this.clearAlerts();
      this.alert[type] = true;
      this.message = message;
      if (type === 'success' || type === 'info') {
        setTimeout(() => this.clearAlerts(), 5000);
      }
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
        this.selectedFile = file;
        this.parseExcelFile(file);
      } else {
        this.showAlert('failed', 'Please select a valid Excel file');
      }
    },

    handleDrop(event) {
      const file = event.dataTransfer.files[0];
      if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
        this.selectedFile = file;
        this.parseExcelFile(file);
      }
    },

    parseExcelFile(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

          this.processExcelData(jsonData);
        } catch (error) {
          this.showAlert('failed', 'Error reading Excel file: ' + error.message);
        }
      };
      reader.readAsArrayBuffer(file);
    },

    async validateKebeles(uniqueKebeles, members) {
      this.kebeleMappings = [];
      this.validatingKebeles = true;
      this.totalKebelesToValidate = uniqueKebeles.length;
      this.kebeleValidationProgress = 0;

      for (let i = 0; i < uniqueKebeles.length; i++) {
        const kebeleName = uniqueKebeles[i];

        const suggestions = await this.searchSimilarKebeles(kebeleName);

        // Update progress
        this.kebeleValidationProgress = i + 1;

        if (suggestions.length === 1) {
          this.kebeleMap.set(kebeleName, suggestions[0]);
        } else if (suggestions.length === 0 || suggestions.length > 1) {
          const affectedRows = members.filter(m => m.kebele === kebeleName).length;

          this.kebeleMappings.push({
            inputName: kebeleName,
            affectedRows: affectedRows,
            suggestions: suggestions,
            searchResults: [],
            searchTerm: kebeleName,
            selectedKebele: null,
            applied: false,
            skipped: false
          });
        }
      }

      this.validatingKebeles = false;

      if (this.kebeleMappings.length > 0) {
        this.showKebeleMapping = true;
      } else {
        // No kebeles need mapping, proceed directly
        this.validateAllFamilies();
        this.currentStep = 3;
      }
    },

    async searchSimilarKebeles(kebeleName) {
      try {
        const data = {
          function: "getAvailableKebeles",
          search: kebeleName
        };

        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          return res.data.data.kebeles || [];
        }
        return [];
      } catch (error) {
        return [];
      }
    },

    applyKebeleMapping(mappingData) {
      this.kebeleMap.set(mappingData.inputName, mappingData.selectedKebele);
    },

    skipKebeleRows(skipData) {
      this.skippedKebeles.add(skipData.inputName);
    },

    proceedAfterKebeleMapping() {
      this.showKebeleMapping = false;

      const validMembers = this.tempMembers.filter(member => {
        return !this.skippedKebeles.has(member.kebele);
      });

      if (validMembers.length === 0) {
        this.showAlert('failed', 'No valid members after filtering');
        return;
      }

      // Re-group families with valid members only
      this.groupMembersByFamily(validMembers);
      this.showAlert('success', `Processing ${validMembers.length} members in ${this.families.length} families`);

      // Now proceed to preview (step 3)
      this.validateAllFamilies();
      this.currentStep = 3;
    },

    processExcelData(jsonData) {
      if (jsonData.length < 10) {
        this.showAlert('failed', 'Excel file must contain data rows');
        return;
      }

      const members = [];
      for (let i = 10; i < jsonData.length; i++) {
        const row = jsonData[i];
        if (!row || !row[0] || !row[0].toString().trim()) continue;

        const translatedRow = this.translateRowData(row);
        const fullName = translatedRow[0] || '';

        let year = parseInt(translatedRow[3]) || new Date().getFullYear();
        if (year < 100) year = year < 50 ? 2000 + year : 1900 + year;
        else if (year > 2500) year = year - 7;

        const dateOfBirth = `${year}-${(translatedRow[2] || 1).toString().padStart(2, '0')}-${(translatedRow[1] || 1).toString().padStart(2, '0')}`;

        const hasCbhiCard = this.parseCbhiCardStatus(translatedRow[16] || row[16]);
        const householdId = translatedRow[5] || '';

        members.push({
          rowIndex: i,
          originalRowNumber: i + 1,
          householdCbhiId: householdId,
          fullName: fullName,
          originalFullName: row[0] || '',
          dateOfBirth: dateOfBirth,
          gender: this.normalizeGender(translatedRow[4]),
          kebele: translatedRow[7] || '',
          originalKebele: row[7] || '',
          gote: translatedRow[8] || '',
          relationship: this.normalizeRelationship(translatedRow[9]),
          originalRelationship: row[9] || '',
          profession: translatedRow[10] || '',
          phoneNumber: '',
          email: '',
          nationalId: '',
          cbhiCardIssued: hasCbhiCard,
          cardIssueNotes: hasCbhiCard ? householdId : ''
        });
      }

      if (members.length === 0) {
        this.showAlert('failed', 'No valid members found');
        return;
      }

      this.tempMembers = members;

      // Group families and proceed to plan selection immediately
      this.groupMembersByFamily(members);
      this.showAlert('success', `Loaded ${members.length} members in ${this.families.length} families`);
      this.loadPlans();
    },

    groupMembersByFamily(members) {
      const familyMap = new Map();
      let lastHouseholdId = null;

      members.forEach(member => {
        let householdId = member.householdCbhiId;

        if (!householdId || householdId.toString().trim() === '') {
          if (lastHouseholdId) {
            householdId = lastHouseholdId;
          } else {
            householdId = `FAMILY_${member.rowIndex}`;
          }
        } else {
          lastHouseholdId = householdId;
        }

        if (!familyMap.has(householdId)) {
          familyMap.set(householdId, []);
        }
        familyMap.get(householdId).push(member);
      });

      this.families = Array.from(familyMap.entries()).map(([householdId, members]) => {
        members.sort((a, b) => {
          if (a.relationship === 'head') return -1;
          if (b.relationship === 'head') return 1;
          return 0;
        });

        return {
          householdId,
          members,
          expectedPayment: 0,
          freeMembers: 0,
          additionalMembers: 0,
          additionalMemberCost: 0
        };
      });

      this.totalMembers = members.length;
    },

    async loadPlans() {
      this.loadingPlans = true;
      this.currentStep = 2;

      try {
        const data = { function: "getAvailablePlans" };
        const res = await executeJson(data, authUrl);

        if (res.data && res.data.success) {
          this.availablePlans = res.data.data.plans || [];
        } else {
          this.showAlert('failed', 'Failed to load plans');
        }
      } catch (error) {
        this.showAlert('failed', 'Error loading plans: ' + error.message);
      } finally {
        this.loadingPlans = false;
      }
    },

    selectPlan(plan) {
      this.selectedPlan = plan;
      this.calculateExpectedPayments();
    },

    calculateExpectedPayments() {
      if (!this.selectedPlan) return;

      const planPrimary = parseFloat(this.selectedPlan.primary_annual_amount);
      const additionalRate = parseFloat(this.selectedPlan.additional_member_rate);
      const freeSpouses = this.selectedPlan.beneficiary_rules.free_immediate_family.included_spouses;
      const ageThreshold = this.selectedPlan.beneficiary_rules.age_threshold;
      const freeChildrenUnder = this.selectedPlan.beneficiary_rules.free_immediate_family.children_under_age > 0;

      this.families.forEach(family => {
        let freeMembers = 0;
        let additionalMembers = 0;
        let spouseCount = 0;

        family.members.forEach(member => {
          if (member.relationship === 'head') {
            return;
          }

          const age = this.calculateAge(member.dateOfBirth);

          if (member.relationship === 'spouse') {
            spouseCount++;
            if (freeSpouses === 'unlimited' || spouseCount <= parseInt(freeSpouses)) {
              freeMembers++;
            } else {
              additionalMembers++;
            }
          } else if (member.relationship === 'child') {
            if (freeChildrenUnder && age < ageThreshold) {
              freeMembers++;
            } else {
              additionalMembers++;
            }
          } else {
            additionalMembers++;
          }
        });

        const additionalCost = planPrimary * additionalRate;
        family.freeMembers = freeMembers;
        family.additionalMembers = additionalMembers;
        family.additionalMemberCost = additionalCost;
        family.expectedPayment = planPrimary + (additionalMembers * additionalCost);
      });

      this.totalExpectedPayment = this.families.reduce((sum, f) => sum + f.expectedPayment, 0);
    },

    calculateAge(dob) {
      const today = new Date();
      const birthDate = new Date(dob);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    },

    async proceedToPreview() {
      if (!this.selectedPlan) {
        this.showAlert('failed', 'Please select a plan');
        return;
      }

      // Validate kebeles before proceeding to preview
      const uniqueKebeles = [...new Set(this.tempMembers.map(m => m.kebele).filter(k => k))];
      await this.validateKebeles(uniqueKebeles, this.tempMembers);
    },

    validateAllFamilies() {
      this.validationErrors = [];

      this.families.forEach(family => {
        family.members.forEach(member => {
          const row = member.originalRowNumber;
          if (!member.fullName || !member.fullName.trim()) {
            this.validationErrors.push(`Row ${row}: Full name is required`);
          }
          if (!member.gender) this.validationErrors.push(`Row ${row}: Gender required`);
          if (!member.dateOfBirth) this.validationErrors.push(`Row ${row}: DOB required`);
          if (!member.kebele) this.validationErrors.push(`Row ${row}: Kebele required`);
        });
      });
    },

    async startUpload() {
      if (this.validationErrors.length > 0) {
        this.showAlert('failed', 'Please fix validation errors');
        return;
      }

      if (!this.paymentStatus) {
        this.showAlert('failed', 'Please select payment status');
        return;
      }

      if (!this.uploadMode) {
        this.showAlert('failed', 'Please select upload processing mode');
        return;
      }

      if (this.uploadMode === 'background') {
        await this.startBackgroundUpload();
      } else {
        await this.startRealtimeUpload();
      }
    },

    async startBackgroundUpload() {
      this.processing = true;
      this.currentStep = 4;

      try {
        const uploadData = {
          function: "bulkUploadMembersBackground",
          families: JSON.stringify(this.families),
          selectedPlanId: this.selectedPlan.plan_id,
          paymentStatus: this.paymentStatus,
          kebeleMap: JSON.stringify(Object.fromEntries(this.kebeleMap)),
          totalFamilies: this.families.length,
          totalMembers: this.totalMembers,
          fileName: this.selectedFile?.name || 'upload.xlsx'
        };

        const res = await executeJson(uploadData, authUrl);

        if (res.data && res.data.success) {
          this.backgroundJobId = res.data.data.jobId;
          this.showAlert('success', `Upload queued for background processing. Job ID: ${this.backgroundJobId}`);

          this.startPolling();
        } else {
          this.showAlert('failed', res.data?.message || 'Failed to queue background upload');
          this.currentStep = 3;
        }
      } catch (error) {
        this.showAlert('failed', 'Error uploading file: ' + error.message);
        this.currentStep = 3;
      } finally {
        this.processing = false;
      }
    },

    startPolling() {
      this.pollingInterval = setInterval(async () => {
        try {
          const data = {
            function: "getBackgroundJobStatus",
            jobId: this.backgroundJobId
          };

          const res = await executeJson(data, authUrl);

          if (res.data && res.data.success) {
            const job = res.data.data;

            this.uploadProgress = parseFloat(job.progress) || 0;
            this.uploadResults.successfulFamilies = parseInt(job.successful_families) || 0;
            this.uploadResults.failedFamilies = parseInt(job.failed_families) || 0;
            this.uploadResults.successfulMembers = parseInt(job.successful_members) || 0;
            this.uploadResults.failedMembers = parseInt(job.failed_members) || 0;

            if (job.status === 'completed' || job.status === 'failed') {
              this.stopPolling();

              if (job.status === 'completed') {
                this.uploadResults.familyDetails = JSON.parse(job.results || '[]');
                this.currentStep = 5;
                this.showAlert('success', 'Background processing completed!');
              } else {
                this.showAlert('failed', 'Background processing failed: ' + job.error_message);
                this.currentStep = 3;
              }
            }
          }
        } catch (error) {
          console.error('Polling error:', error);
        }
      }, 3000);
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },

    async startRealtimeUpload() {
      this.processing = true;
      this.uploadPaused = false;
      this.uploadCancelled = false;
      this.currentStep = 4;
      this.processedCount = 0;
      this.currentProcessingFamily = 0;
      this.uploadProgress = 0;
      this.uploadStartTime = Date.now();
      this.totalPausedTime = 0;
      this.recentActivity = [];

      this.uploadResults = {
        successfulFamilies: 0,
        failedFamilies: 0,
        successfulMembers: 0,
        failedMembers: 0,
        familyDetails: []
      };

      this.startTimer();
      await this.processNextFamily(0);
    },

    async processNextFamily(familyIndex) {
      if (this.uploadPaused || this.uploadCancelled) {
        return;
      }

      if (familyIndex >= this.families.length) {
        this.completeUpload();
        return;
      }

      this.currentProcessingFamily = familyIndex + 1;
      const family = this.families[familyIndex];
      this.currentFamilyProcessing = family;

      const familyResult = await this.uploadFamily(family);
      this.uploadResults.familyDetails.push(familyResult);

      if (familyResult.success) {
        this.uploadResults.successfulFamilies++;
      } else {
        this.uploadResults.failedFamilies++;
      }

      this.recentActivity.unshift({
        ...familyResult,
        timestamp: new Date().toLocaleTimeString()
      });
      if (this.recentActivity.length > 10) {
        this.recentActivity.pop();
      }

      this.uploadProgress = ((familyIndex + 1) / this.families.length) * 100;

      if (!this.uploadPaused && !this.uploadCancelled) {
        await new Promise(resolve => setTimeout(resolve, 500));
        await this.processNextFamily(familyIndex + 1);
      }
    },

    async uploadFamily(family) {
      const familyResult = {
        householdId: family.householdId,
        success: true,
        members: [],
        successCount: 0,
        failureCount: 0
      };

      const headMember = family.members.find(m => m.relationship === 'head');

      if (!headMember) {
        familyResult.success = false;
        familyResult.failureCount = family.members.length;
        family.members.forEach(member => {
          familyResult.members.push({
            name: member.fullName,
            relationship: member.relationship,
            success: false,
            cbhiNumber: '',
            message: 'No household head found'
          });
          this.uploadResults.failedMembers++;
        });
        this.processedCount += family.members.length;
        return familyResult;
      }

      const beneficiaries = family.members
          .filter(m => m.relationship !== 'head')
          .map(m => ({
            fullName: m.fullName,
            gender: m.gender,
            dateOfBirth: m.dateOfBirth,
            relationship: m.relationship,
            idNumber: m.nationalId || ''
          }));

      const familyComposition = {
        spouse: 0,
        childrenUnder18: 0,
        additionalSpouses: 0,
        childrenOver18: 0,
        otherRelatives: 0
      };

      const ageThreshold = this.selectedPlan.beneficiary_rules?.age_threshold || 18;
      const freeSpousesCount = this.selectedPlan.beneficiary_rules?.free_immediate_family?.included_spouses || 1;
      let spouseCount = 0;

      family.members.forEach(member => {
        if (member.relationship === 'head') return;

        const age = this.calculateAge(member.dateOfBirth);

        if (member.relationship === 'spouse' || member.relationship === 'husband' || member.relationship === 'wife') {
          spouseCount++;
          if (freeSpousesCount === 'unlimited' || spouseCount <= parseInt(freeSpousesCount)) {
            familyComposition.spouse++;
          } else {
            familyComposition.additionalSpouses++;
          }
        } else if (member.relationship === 'child' || member.relationship === 'son' || member.relationship === 'daughter') {
          if (age < ageThreshold) {
            familyComposition.childrenUnder18++;
          } else {
            familyComposition.childrenOver18++;
          }
        } else {
          familyComposition.otherRelatives++;
        }
      });

      try {
        this.currentProcessingMember = 1;
        const result = await this.registerFamilyWithBeneficiaries(headMember, beneficiaries, familyComposition, family.members.length);

        if (result.success) {
          this.uploadResults.successfulMembers += family.members.length;
          familyResult.successCount = family.members.length;

          familyResult.members.push({
            name: headMember.fullName,
            relationship: 'head',
            success: true,
            cbhiNumber: result.cbhiNumber || '',
            message: 'Registered successfully'
          });

          beneficiaries.forEach((ben) => {
            familyResult.members.push({
              name: ben.fullName,
              relationship: ben.relationship,
              success: true,
              cbhiNumber: '',
              message: 'Registered as beneficiary'
            });
          });

          this.processedCount += family.members.length;
        } else {
          throw new Error(result.message || 'Family registration failed');
        }
      } catch (error) {
        this.uploadResults.failedMembers += family.members.length;
        familyResult.failureCount = family.members.length;
        familyResult.success = false;

        family.members.forEach(member => {
          familyResult.members.push({
            name: member.fullName,
            relationship: member.relationship,
            success: false,
            cbhiNumber: '',
            message: error.message
          });
        });

        this.processedCount += family.members.length;
      }

      return familyResult;
    },

    async registerFamilyWithBeneficiaries(headMember, beneficiaries, familyComposition, householdSize) {
      let locationIds = {
        regionId: 1,
        zoneId: 1,
        woredaId: 1,
        kebeleId: 1
      };

      if (this.kebeleMap && this.kebeleMap.has(headMember.kebele)) {
        const kebeleData = this.kebeleMap.get(headMember.kebele);
        locationIds = {
          regionId: parseInt(kebeleData.region_id),
          zoneId: parseInt(kebeleData.zone_id),
          woredaId: parseInt(kebeleData.woreda_id),
          kebeleId: parseInt(kebeleData.kebele_id)
        };
      }

      const memberData = {
        function: "registerMember",
        enrollmentType: "cbhi_member",
        selectedPlanId: this.selectedPlan.plan_id.toString(),
        householdId: "",
        relationshipToHead: "head",
        isHEWRegistration: false,

        title: headMember.title || "",
        fullName: headMember.fullName,

        dateOfBirth: headMember.dateOfBirth,
        gender: headMember.gender,
        nationalId: headMember.nationalId || "",

        maritalStatus: this.getMaritalStatus(headMember, beneficiaries),
        occupation: headMember.occupation || headMember.profession || "",
        monthlyIncome: headMember.monthlyIncome || 0,
        householdSize: householdSize,

        regionId: locationIds.regionId,
        zoneId: locationIds.zoneId,
        woredaId: locationIds.woredaId,
        kebeleId: locationIds.kebeleId,
        gote: headMember.gote || "",
        address: headMember.kebele,

        phoneNumber: headMember.phoneNumber || "",
        email: headMember.email || "",

        cbhiCardIssued: headMember.cbhiCardIssued || false,
        cardIssueNotes: headMember.cardIssueNotes || "",

        beneficiaries: beneficiaries,
        familyComposition: familyComposition,

        membersHavePaid: this.paymentStatus === 'paid',

        collectionPoint: "Main Office"
      };

      try {
        const res = await executeJson(memberData, authUrl);

        if (res.data && res.data.success) {
          return {
            success: true,
            cbhiNumber: res.data.data.cbhiNumber,
            householdId: res.data.data.householdId,
            memberId: res.data.data.memberId
          };
        } else {
          return {
            success: false,
            message: res.data?.message || 'Registration failed'
          };
        }
      } catch (error) {
        return {
          success: false,
          message: error.message
        };
      }
    },

    getMaritalStatus(headMember, beneficiaries) {
      const hasSpouse = beneficiaries.some(b =>
          ['spouse', 'husband', 'wife'].includes(b.relationship.toLowerCase())
      );

      if (hasSpouse) return 'married';

      const age = this.calculateAge(headMember.dateOfBirth);
      if (age < 30) return 'single';

      return 'married';
    },

    translateRowData(row) {
      return row.map(cell => {
        if (typeof cell === 'string') {
          return this.translateAmharicText(cell);
        }
        return cell;
      });
    },

    translateAmharicText(text) {
      if (!text) return '';
      return text;
    },

    normalizeGender(gender) {
      if (!gender) return '';
      const g = gender.toLowerCase();
      if (g.includes('m') || g.includes('male') || g.includes('ወንድ')) return 'male';
      if (g.includes('f') || g.includes('female') || g.includes('ሴት')) return 'female';
      return gender;
    },

    normalizeRelationship(rel) {
      if (!rel) return 'other';
      const r = rel.toLowerCase();
      if (r.includes('head') || r.includes('ሃላፊ')) return 'head';
      if (r.includes('spouse') || r.includes('wife') || r.includes('husband') || r.includes('ባለቤት')) return 'spouse';
      if (r.includes('child') || r.includes('son') || r.includes('daughter') || r.includes('ልጅ')) return 'child';
      if (r.includes('parent') || r.includes('father') || r.includes('mother')) return 'parent';
      return 'other';
    },

    parseCbhiCardStatus(value) {
      if (!value) return false;
      const v = value.toString().toLowerCase().trim();
      if (v === 'yes' || v === 'y' || v === 'true' || v === '1') return true;
      if (v.includes('አዎ')) return true;
      return false;
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        return new Date(dateString).toLocaleDateString();
      } catch {
        return 'Invalid';
      }
    },

    formatTime(seconds) {
      if (!seconds || seconds < 0) return '0s';

      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;

      if (hours > 0) {
        return `${hours}h ${minutes}m ${secs}s`;
      } else if (minutes > 0) {
        return `${minutes}m ${secs}s`;
      } else {
        return `${secs}s`;
      }
    },

    startTimer() {
      this.timerInterval = setInterval(() => {
        if (!this.uploadPaused) {
          const currentTime = Date.now();
          this.elapsedTime = Math.floor((currentTime - this.uploadStartTime - this.totalPausedTime) / 1000);
        }
      }, 1000);
    },

    stopTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    },

    pauseUpload() {
      this.uploadPaused = true;
      this.uploadPauseTime = Date.now();
    },

    resumeUpload() {
      if (this.uploadPauseTime) {
        const pauseDuration = Date.now() - this.uploadPauseTime;
        this.totalPausedTime += pauseDuration;
        this.uploadPauseTime = null;
      }

      this.uploadPaused = false;
      const nextIndex = this.uploadResults.successfulFamilies + this.uploadResults.failedFamilies;
      this.processNextFamily(nextIndex);
    },

    cancelUpload() {
      if (confirm('Are you sure you want to cancel the upload? Progress will be saved for already uploaded families.')) {
        this.uploadCancelled = true;
        this.processing = false;
        this.stopTimer();
        this.completeUpload();
      }
    },

    completeUpload() {
      this.processing = false;
      this.uploadPaused = false;
      this.currentFamilyProcessing = null;
      this.stopTimer();
      this.currentStep = 5;
      this.showAlert('success', 'Upload completed!');
    },

    viewBackgroundJobs() {
      window.location.href = '/background-jobs';
    },

    getRowClass(member) {
      if (member.relationship === 'head') return 'table-warning';
      if (member.relationship === 'spouse') return 'table-info';
      if (member.relationship === 'child') return 'table-light';
      return '';
    },

    getRelationshipBadgeClass(relationship) {
      const classes = 'badge ';
      if (relationship === 'head') return classes + 'bg-warning text-dark';
      if (relationship === 'spouse') return classes + 'bg-info';
      if (relationship === 'child') return classes + 'bg-secondary';
      return classes + 'bg-light text-dark';
    },

    hasValidationError(member) {
      return this.validationErrors.some(e => e.includes(`Row ${member.originalRowNumber}:`));
    },

    goBack() {
      this.currentStep = 1;
      this.resetData();
    },

    resetUpload() {
      this.currentStep = 1;
      this.resetData();
    },

    resetData() {
      this.families = [];
      this.totalMembers = 0;
      this.selectedPlan = null;
      this.validationErrors = [];
      this.uploadPaused = false;
      this.uploadCancelled = false;
      this.currentProcessingFamily = 0;
      this.elapsedTime = 0;
      this.totalPausedTime = 0;
      this.recentActivity = [];
      this.uploadMode = 'realtime';
      this.paymentStatus = null;
      this.backgroundJobId = null;
      this.uploadResults = {
        successfulFamilies: 0,
        failedFamilies: 0,
        successfulMembers: 0,
        failedMembers: 0,
        familyDetails: []
      };
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },

    downloadResults() {
      const ws_data = [
        ['Family ID', 'Member Name', 'Relationship', 'Status', 'CBHI Number', 'Message']
      ];

      this.uploadResults.familyDetails.forEach(family => {
        if (family.members && Array.isArray(family.members)) {
          family.members.forEach(member => {
            ws_data.push([
              family.householdId,
              member.name,
              member.relationship,
              member.success ? 'Success' : 'Failed',
              member.cbhiNumber || '',
              member.message
            ]);
          });
        }
      });

      const ws = XLSX.utils.aoa_to_sheet(ws_data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Upload Results");
      XLSX.writeFile(wb, `cbhi_upload_results_${new Date().toISOString().split('T')[0]}.xlsx`);
    }
  }
};
</script>

<style scoped>
.dropzone-wrapper {
  border: 2px dashed #007bff;
  border-radius: 10px;
  background: rgba(0, 123, 255, 0.05);
  text-align: center;
  padding: 40px 20px;
  margin: 20px 0;
  transition: all 0.3s ease;
}

.dropzone-wrapper:hover {
  border-color: #0056b3;
  background: rgba(0, 123, 255, 0.1);
}

.table-warning {
  background-color: #fff3cd !important;
}

.table-info {
  background-color: #d1ecf1 !important;
}

.accordion-button:not(.collapsed) {
  background-color: #f8f9fa;
}

.activity-item {
  border-radius: 5px;
  transition: all 0.2s ease;
}

.activity-item:hover {
  transform: translateX(5px);
}

.bg-success-subtle {
  background-color: rgba(25, 135, 84, 0.1) !important;
}

.bg-danger-subtle {
  background-color: rgba(220, 53, 69, 0.1) !important;
}

.bg-info-subtle {
  background-color: rgba(13, 202, 240, 0.1) !important;
}

.bg-primary-subtle {
  background-color: rgba(13, 110, 253, 0.1) !important;
}

.activity-log {
  border: 1px solid #dee2e6;
  border-radius: 5px;
  padding: 10px;
  background-color: #f8f9fa;
}

.progress {
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}

.payment-option, .processing-mode-option {
  transition: all 0.3s ease;
  min-height: 120px;
}

.payment-option:hover, .processing-mode-option:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}
</style>